import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Form, Alert, Card, Spinner } from 'react-bootstrap';
import { Gear, KeyFill, EnvelopeFill } from 'react-bootstrap-icons';

interface LoginProps {
  onSwitchToRegister: () => void;
  /** Shown above the form, e.g. "Your session has ended…" after a forced sign-out. */
  notice?: string;
}

/**
 * The form stays mounted while signing in (spec 001): a failure shows the
 * server's message and keeps the typed email. On success the Auth page
 * navigates on.
 */
const Login: React.FC<LoginProps> = ({ onSwitchToRegister, notice }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);

    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sm-auth">
      {/* Background decorative gears */}
      <div className="sm-auth__bg-gear sm-auth__bg-gear--top-left">
        <Gear size={300} className="sm-animate-gear" />
      </div>
      <div className="sm-auth__bg-gear sm-auth__bg-gear--bottom-right">
        <Gear size={250} className="sm-animate-gear sm-animate-gear--reverse" />
      </div>
      <div className="sm-auth__bg-gear sm-auth__bg-gear--center">
        <Gear size={600} className="sm-animate-gear sm-animate-gear--slow" />
      </div>

      <Card className="sm-auth__card">
        {/* Decorative elements */}
        <div className="sm-auth__card-line sm-auth__card-line--top" />
        <div className="sm-auth__rivet sm-auth__rivet--left" />
        <div className="sm-auth__rivet sm-auth__rivet--right" />
        
        <Card.Body className="sm-auth__card-body">
          <div className="sm-auth__header">
            <Gear size={48} className="sm-auth__icon" />
            <h2 className="sm-auth__title">Access the Forge</h2>
            <p className="sm-auth__subtitle">
              Or{' '}
              <button type="button" className="sm-auth__link" onClick={onSwitchToRegister}>
                forge a new account
              </button>
            </p>
          </div>

          {notice && (
            <div className="sm-auth__notice" role="status">
              {notice}
            </div>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="sm-auth__form-group">
              <Form.Label className="sm-auth__label">
                <EnvelopeFill className="sm-auth__label-icon" />
                Email Address
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="sm-auth__input"
              />
            </Form.Group>

            <Form.Group className="sm-auth__form-group">
              <Form.Label className="sm-auth__label">
                <KeyFill className="sm-auth__label-icon" />
                Password
              </Form.Label>
              <Form.Control
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="sm-auth__input"
              />
            </Form.Group>

            {error && <Alert className="sm-auth__alert" role="alert">{error}</Alert>}

            <Button type="submit" className="sm-auth__submit" disabled={busy} aria-busy={busy}>
              {busy ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="sm-auth__submit-spinner" />
                  Authenticating...
                </>
              ) : (
                '⚙ Enter the Forge'
              )}
            </Button>
          </Form>
        </Card.Body>
        
        <div className="sm-auth__card-line sm-auth__card-line--bottom" />
      </Card>
    </div>
  );
};

export default Login;